import { useState } from "react";
import { api } from "@/lib/api";
import { ReportShell, Stat, Table, money } from "./ReportShell";

const ymd = (d) => d.toISOString().slice(0, 10);
const yearStart = () => ymd(new Date(new Date().getFullYear(), 0, 1));
const monthLabel = (m) =>
  m && m.length === 7
    ? new Date(`${m}-15`).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : m || "—";

export const SalesReport = () => {
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/sales", { params: { from, to } });
      setData(data);
    } finally {
      setBusy(false);
    }
  };
  const t = data?.totals;
  const months = data?.months || [];
  const colors = data?.colors || [];
  const customers = data?.customers || [];
  const people = data?.people || [];
  const max = Math.max(1, ...months.map((m) => m.invoiced));

  return (
    <ReportShell
      id="sales"
      title="Sales Report"
      blurb="Invoiced and collected by month (voided invoices excluded), by material, by customer and by salesperson."
      range
      from={from}
      to={to}
      onFrom={setFrom}
      onTo={setTo}
      onGenerate={generate}
      busy={busy}
      generated={!!data}
      csvRows={months}
      csvName={`sales-${from}-${to}.csv`}
    >
      {t && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Stat label="Invoices" value={t.invoices} testId="sales-stat-invoices" />
          <Stat label="Invoiced" value={money(t.invoiced)} tone="text-yellow-400" testId="sales-stat-invoiced" />
          <Stat label="Collected" value={money(t.paid)} tone="text-emerald-400" testId="sales-stat-paid" />
          <Stat label="Outstanding" value={money(t.outstanding)} tone={t.outstanding > 0 ? "text-amber-300" : "text-zinc-300"} testId="sales-stat-outstanding" />
          <Stat label="Avg invoice" value={money(t.average)} testId="sales-stat-average" />
        </div>
      )}
      <div className="mt-4 grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Table
          testId="sales-months"
          rowKey={(r) => r.month}
          rows={months}
          empty="No invoices in this range."
          columns={[
            {
              key: "month",
              label: "Month",
              render: (r) => (
                <div className="min-w-[140px]">
                  <div className="font-semibold">{monthLabel(r.month)}</div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-800">
                    <div className="h-full rounded-full bg-yellow-500" style={{ width: `${(r.invoiced / max) * 100}%` }} />
                  </div>
                </div>
              ),
            },
            { key: "invoices", label: "#", right: true },
            { key: "invoiced", label: "Invoiced", right: true, render: (r) => money(r.invoiced) },
            { key: "paid", label: "Collected", right: true, render: (r) => <span className="text-emerald-400">{money(r.paid)}</span> },
            { key: "outstanding", label: "Open", right: true, render: (r) => <span className={r.outstanding > 0 ? "text-amber-300" : "text-zinc-500"}>{money(r.outstanding)}</span> },
          ]}
        />
        <Table
          testId="sales-colors"
          rowKey={(r) => r.slab_color}
          rows={colors}
          empty="No material data."
          columns={[
            { key: "slab_color", label: "Material", render: (r) => <span className="font-semibold">{r.slab_color}</span> },
            { key: "invoices", label: "#", right: true },
            { key: "invoiced", label: "Invoiced", right: true, render: (r) => money(r.invoiced) },
          ]}
        />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Table
          testId="sales-customers"
          rowKey={(r) => r.customer}
          rows={customers}
          empty="No customers in this range."
          columns={[
            { key: "customer", label: "Sales by Customer", render: (r) => <span className="font-semibold">{r.customer}</span> },
            { key: "invoices", label: "#", right: true },
            { key: "invoiced", label: "Invoiced", right: true, render: (r) => money(r.invoiced) },
            { key: "paid", label: "Collected", right: true, render: (r) => <span className="text-emerald-400">{money(r.paid)}</span> },
            { key: "outstanding", label: "Open", right: true, render: (r) => <span className={r.outstanding > 0 ? "text-amber-300" : "text-zinc-500"}>{money(r.outstanding)}</span> },
          ]}
        />
        <Table
          testId="sales-people"
          rowKey={(r) => r.person}
          rows={people}
          empty="No salesperson data."
          columns={[
            { key: "person", label: "Sales by Person", render: (r) => <span className="font-semibold">{r.person}</span> },
            { key: "invoices", label: "#", right: true },
            { key: "invoiced", label: "Invoiced", right: true, render: (r) => money(r.invoiced) },
          ]}
        />
      </div>
    </ReportShell>
  );
};
